/* ============================================================
   GOOSIUS THEME — MAIN JS
   ============================================================ */

'use strict';

// ---- CART DRAWER ----
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
  }

  connectedCallback() {
    this.overlay = document.querySelector('.overlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:update', () => this.renderContents());
  }

  open() {
    this.classList.add('open');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.renderContents();
    setTimeout(() => this.querySelector('[data-close]')?.focus(), 100);
  }

  close() {
    this.classList.remove('open');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
    this.isOpen = false;
  }

  async renderContents() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderCart(cart);
    } catch (e) {
      console.error('Cart fetch error:', e);
    }
  }

  renderCart(cart) {
    const countEl = this.querySelector('[data-cart-count]');
    const itemsEl = this.querySelector('[data-cart-items]');
    const emptyEl = this.querySelector('[data-cart-empty]');
    const subtotalEl = this.querySelector('[data-cart-subtotal]');
    const checkoutBtn = this.querySelector('[data-checkout-btn]');

    if (countEl) countEl.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';

    if (cart.item_count === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      if (itemsEl) itemsEl.style.display = 'none';
      if (checkoutBtn) checkoutBtn.style.display = 'none';
    } else {
      if (emptyEl) emptyEl.style.display = 'none';
      if (itemsEl) {
        itemsEl.style.display = 'flex';
        itemsEl.innerHTML = cart.items.map(item => this.renderItem(item)).join('');
        itemsEl.querySelectorAll('[data-remove]').forEach(btn => {
          btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
        });
        itemsEl.querySelectorAll('[data-qty]').forEach(input => {
          input.addEventListener('change', (e) => this.updateQty(input.dataset.key, parseInt(e.target.value)));
        });
      }
      if (checkoutBtn) checkoutBtn.style.display = '';
    }

    if (subtotalEl) {
      subtotalEl.textContent = Theme.formatMoney(cart.total_price);
    }

    document.querySelectorAll('[data-cart-icon-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  }

  renderItem(item) {
    const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
    return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          ${item.image ? `<img src="${item.image.replace('.jpg', '_200x.jpg')}" alt="${item.product_title}" loading="lazy">` : ''}
        </div>
        <div class="cart-item__details">
          ${item.vendor ? `<span class="cart-item__vendor">${item.vendor}</span>` : ''}
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${hasVariant ? `<span class="cart-item__variant">${item.variant_title}</span>` : ''}
          <div class="cart-item__price-qty">
            <div class="quantity-selector" style="transform:scale(0.85);transform-origin:left">
              <button class="quantity-selector__btn" data-action="decrease" data-key="${item.key}" onclick="this.closest('.quantity-selector').querySelector('[data-qty]').value = Math.max(0, parseInt(this.closest('.quantity-selector').querySelector('[data-qty]').value)-1); this.closest('.quantity-selector').querySelector('[data-qty]').dispatchEvent(new Event('change'))">−</button>
              <input type="number" class="quantity-selector__input" value="${item.quantity}" min="0" data-qty data-key="${item.key}">
              <button class="quantity-selector__btn" data-action="increase" data-key="${item.key}" onclick="this.closest('.quantity-selector').querySelector('[data-qty]').value = parseInt(this.closest('.quantity-selector').querySelector('[data-qty]').value)+1; this.closest('.quantity-selector').querySelector('[data-qty]').dispatchEvent(new Event('change'))">+</button>
            </div>
            <span class="price">${Theme.formatMoney(item.line_price)}</span>
          </div>
        </div>
        <button class="cart-item__remove" data-remove data-key="${item.key}" aria-label="Remove ${item.product_title}">×</button>
      </div>
    `;
  }

  async removeItem(key) {
    await Theme.updateCart({ updates: { [key]: 0 } });
    await this.renderContents();
  }

  async updateQty(key, qty) {
    await Theme.updateCart({ updates: { [key]: qty } });
    await this.renderContents();
  }
}

customElements.define('cart-drawer', CartDrawer);

// ---- HEADER ----
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.scrollHandler = () => {
      if (window.scrollY > 40) {
        this.classList.add('scrolled');
      } else {
        this.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.scrollHandler();

    // Cart icon click
    this.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
    });

    // Mobile menu toggle
    const menuToggle = this.querySelector('[data-menu-toggle]');
    const mobileMenu = this.querySelector('[data-mobile-menu]');
    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        const isOpen = mobileMenu.classList.toggle('open');
        menuToggle.setAttribute('aria-expanded', isOpen);
        document.body.style.overflow = isOpen ? 'hidden' : '';
        this.querySelector('.overlay')?.classList.toggle('active', isOpen);
      });
    }

    // Search toggle
    const searchToggle = this.querySelector('[data-search-toggle]');
    const searchBar = this.querySelector('[data-search-bar]');
    if (searchToggle && searchBar) {
      searchToggle.addEventListener('click', () => {
        const isOpen = searchBar.classList.toggle('open');
        if (isOpen) searchBar.querySelector('input')?.focus();
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.scrollHandler);
  }
}

customElements.define('site-header', SiteHeader);

// ---- PRODUCT FORM ----
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('[data-submit-btn]');
    this.btnText = this.querySelector('[data-btn-text]');

    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Variant selection
    this.querySelectorAll('[data-variant-option]').forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });

    // Sticky bar button reuses the main form submit (and its variant logic)
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      if (!this.submitBtn?.disabled) this.submitBtn?.click();
    });

    this.updateVariant();
  }

  updateVariant() {
    const form = this.form;
    if (!form) return;

    const selectedOptions = {};
    this.querySelectorAll('[data-variant-option]:checked, [data-variant-option] option:checked').forEach(el => {
      const name = el.closest('[data-option-name]')?.dataset.optionName;
      if (name) selectedOptions[name] = el.value;
    });
    this.querySelectorAll('select[data-variant-option]').forEach(sel => {
      const name = sel.dataset.optionName;
      if (name) selectedOptions[name] = sel.value;
    });

    const variantsData = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    const variant = variantsData.find(v =>
      v.options.every((opt, i) => opt === Object.values(selectedOptions)[i])
    );

    if (variant) {
      const hiddenInput = form.querySelector('[name="id"]');
      if (hiddenInput) hiddenInput.value = variant.id;

      const available = variant.available;
      const addText = this.dataset.addText || 'Add to Cart';
      const soldOutText = this.dataset.soldOutText || 'Sold Out';

      if (this.submitBtn) {
        this.submitBtn.disabled = !available;
        if (this.btnText) {
          this.btnText.textContent = available ? addText : soldOutText;
        }
      }

      // Sync sticky add-to-cart button (if present)
      const stickyBtn = this.querySelector('[data-sticky-add]');
      const stickyBtnText = this.querySelector('[data-sticky-btn-text]');
      if (stickyBtn) stickyBtn.disabled = !available;
      if (stickyBtnText) stickyBtnText.textContent = available ? addText : soldOutText;

      // Update every price element in scope (main + sticky bar)
      this.querySelectorAll('[data-variant-price]').forEach(el => {
        el.textContent = Theme.formatMoney(variant.price);
      });
      this.querySelectorAll('[data-variant-compare-price]').forEach(el => {
        if (variant.compare_at_price > variant.price) {
          el.textContent = Theme.formatMoney(variant.compare_at_price);
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const variantId = formData.get('id');

    if (!variantId) return;

    this.setLoading(true);

    try {
      const qty = parseInt(formData.get('quantity') || '1');
      await Theme.addToCart({ id: variantId, quantity: qty });
      document.dispatchEvent(new CustomEvent('cart:open'));
      Theme.showNotification(this.dataset.addedText || 'Added to cart!');
    } catch (e) {
      Theme.showNotification('Error adding to cart. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (this.submitBtn) {
      this.submitBtn.disabled = loading;
      const spinner = this.submitBtn.querySelector('.spinner');
      const text = this.btnText;
      if (spinner) spinner.style.display = loading ? 'inline-block' : 'none';
      if (text) text.style.opacity = loading ? '0' : '1';
    }
  }
}

customElements.define('product-form', ProductForm);

// ---- ANNOUNCEMENT BAR ----
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const items = this.querySelectorAll('[data-announcement-item]');
    if (items.length <= 1) return;

    let current = 0;
    this.interval = setInterval(() => {
      items[current].classList.remove('active');
      current = (current + 1) % items.length;
      items[current].classList.add('active');
    }, parseInt(this.dataset.interval || '4000'));

    if (items[0]) items[0].classList.add('active');

    const closeBtn = this.querySelector('[data-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.style.display = 'none';
        sessionStorage.setItem('announcement-closed', '1');
      });

      if (sessionStorage.getItem('announcement-closed')) {
        this.style.display = 'none';
      }
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

customElements.define('announcement-bar', AnnouncementBar);

// ---- MEDIA GALLERY (grid on desktop, swipe carousel + dots on mobile) ----
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    if (!this.track || this.dots.length === 0) return;

    // Click a dot -> scroll to that slide
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.slides[i]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });

    // Keep the active dot in sync while swiping (throttled with rAF)
    let ticking = false;
    this.track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { this.syncActiveDot(); ticking = false; });
    }, { passive: true });

    this.syncActiveDot();
  }

  syncActiveDot() {
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let min = Infinity;
    this.slides.forEach((slide, i) => {
      const r = slide.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < min) { min = dist; closest = i; }
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('active', i === closest));
  }
}

customElements.define('media-gallery', MediaGallery);

// ---- STICKY ADD TO CART ----
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector('[data-product-form-trigger]');
    if (!target) return;

    const observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('visible', !entry.isIntersecting);
    }, { threshold: 0 });

    observer.observe(target);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);

// ---- GLOBAL THEME UTILITIES ----
const Theme = {
  moneyFormat: window.Shopify?.money_format || '€{{amount}}',

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  },

  async addToCart(data) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Add to cart failed');
    return response.json();
  },

  async updateCart(data) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  showNotification(message, type = 'success') {
    let toast = document.querySelector('.notification');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'notification';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.background = type === 'error' ? '#C0392B' : 'var(--color-foreground)';
    toast.classList.add('show');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('show'), 3000);
  }
};

window.Theme = Theme;

// ---- QUICK ADD ----
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;
  e.preventDefault();

  const variantId = btn.dataset.variantId;
  if (!variantId) {
    window.location.href = btn.closest('a')?.href || '#';
    return;
  }

  btn.disabled = true;
  try {
    await Theme.addToCart({ id: variantId, quantity: 1 });
    document.dispatchEvent(new CustomEvent('cart:open'));
  } catch {
    Theme.showNotification('Error adding to cart', 'error');
  } finally {
    btn.disabled = false;
  }
});

// ---- INIT ----
document.addEventListener('DOMContentLoaded', () => {
  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const target = document.querySelector(a.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
});
